import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  Output
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { DollarComponent } from '../icons/dollar';
import { LocaleService } from 'src/app/services/locale';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DollarComponent],
  template: `
    <div class="flex flex-col gap-0.5">
      <label *ngIf="label" class="text-[12px] sm:text-[12px] text-[#4a4a4a] font-medium">
        {{ label }}
      </label>
      <div class="flex relative">
        <input
          mask="comma_separator"
          (keyup)="onKeyUp(control, index)"
          (input)="onChange()"
          (keydown)="onKeyDown($event)"
          (paste)="onPaste($event)"
          [type]="type"
          [inputMode]="type === 'number' ? 'numeric' : 'text'"
          [attr.pattern]="type === 'number' ? '[0-9]*' : null"
          class="px-2 py-1.5 sm:py-2 text-[12px] rounded border w-full border-[#D1D5DB] focus:outline-none focus:border-[#043B22] transition-colors placeholder:text-[#9CA3AF]"
          [placeholder]="placeholder"
          [formControl]="control"
          [ngClass]="{
            'border-red-400 focus:border-red-400':
              control.invalid && (control.touched || control.dirty),
            'pr-7': dollar || unit,
            'bg-gray-100 text-[#616161] cursor-not-allowed': control.disabled,
            'bg-white': !control.disabled
          }"
          [required]="required"
        />
        <div *ngIf="dollar" class="text-[#616161] absolute top-1/2 -translate-y-1/2 right-2 pointer-events-none">
          <app-dollar></app-dollar>
        </div>
        <div *ngIf="unit" class="text-[#616161] text-[12px] font-medium absolute top-1/2 -translate-y-1/2 right-2 bg-[#F3F4F6] px-1 py-0.5 rounded">
          {{ unit }}
        </div>
      </div>
      <div
        *ngIf="control.invalid && (control.touched || control.dirty)"
        class="text-red-500 text-[12px] flex items-center gap-0.5"
      >
        <svg class="w-2.5 h-2.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <span>
          <span *ngIf="control.errors?.['required']">
            {{ cleanLabel || (language === 'english' ? 'This field' : 'Questo campo') }}
            {{ locale.common.isRequired }}
          </span>
          <span *ngIf="control.errors?.['email'] || control.errors?.['pattern']">
            {{ cleanLabel || (language === 'english' ? 'This field' : 'Questo campo') }}
            {{ locale.common.isInvalid }}
          </span>
          <span *ngIf="control.errors?.['min']">
            {{ locale.common.valueTooLow }} {{ control.errors?.['min'].min }}
          </span>
          <span *ngIf="control.errors?.['max']">
            {{ locale.common.valueTooHigh }} {{ control.errors?.['max'].max }}
          </span>
          <span *ngIf="control.errors?.['minlength']">
            {{ locale.common.minLengthNew
            }}{{ control.errors?.['minlength'].requiredLength }}
          </span>
          <span *ngIf="control.errors?.['maxlength']">
            {{ locale.common.maxLengthNew
            }}{{ control.errors?.['maxlength'].requiredLength }}
          </span>
          <span *ngIf="control.errors?.['pattern'] && type !== 'email'">
            {{ locale.common.invalidFormat }}
          </span>
          <span *ngIf="control.errors?.['passwordMismatch']">
            {{ locale.common.passwordMismatch }}
          </span>
        </span>
      </div>
    </div>
  `
})
export class InputComponent {
  @Input() label: string = '';
  @Input() unit: string = '';
  @Input() index: number = 0;
  @Input() type: string = 'text';
  @Input() control: FormControl = new FormControl();
  @Input() dollar: boolean = false;
  @Input() placeholder: string = '';
  @Input() keyUp: Function = () => { };
  @Input() required: boolean = false;
  @Output() change = new EventEmitter<any>();
  language = inject(LocaleService).getLanguage();

  private localeService = inject(LocaleService);
  locale = this.localeService.locale();

  constructor() {
    effect(() => {
      this.locale = this.localeService.locale();
    });
  }

  get cleanLabel(): string {
    return (this.label || '').replace(/\s*\(.*?\)\s*/g, '').trim();
  }

  onKeyUp = (control: FormControl, index: number) => {
    this.keyUp(control, index);
  };

  onChange() {
    this.change.emit();
  }

  // Prevent 'e', 'E', '+', '-' in number fields (these are allowed by HTML number input for scientific notation)
  onKeyDown(event: KeyboardEvent) {
    if (this.type === 'number') {
      const forbiddenKeys = ['e', 'E', '+', '-'];
      if (forbiddenKeys.includes(event.key)) {
        event.preventDefault();
      }
    }
  }

  // Prevent pasting non-numeric characters in number fields
  onPaste(event: ClipboardEvent) {
    if (this.type === 'number') {
      const pastedData = event.clipboardData?.getData('text') || '';
      // Only allow digits and decimal point
      if (!/^\d*\.?\d*$/.test(pastedData)) {
        event.preventDefault();
        // Clean the pasted data and set only numeric characters
        const cleanedData = pastedData.replace(/[^\d.]/g, '');
        if (cleanedData) {
          this.control.setValue(cleanedData);
        }
      }
    }
  }
}
